import type { ZodType } from "zod";
import { apiErrorSchema } from "./schemas";

/**
 * A failed call, carrying enough to act on rather than just a message: the field
 * issues a form needs to highlight, and the request id that ties the screen to a
 * line in the ops audit trail.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Array<{ field: string; message: string }> = [],
    readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  fieldMessage(field: string) {
    return this.fields.find((issue) => issue.field === field)?.message;
  }

  get isUnauthorized() {
    return this.status === 401;
  }
}

export interface HttpClientOptions {
  baseUrl: string;
  credentials?: RequestCredentials;
  /** Resolved per call, so a rotated token is picked up without rebuilding the client. */
  getToken?: () => string | null | undefined;
  onUnauthorized?: () => void;
}

export interface RequestOptions<T> {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  credentials?: RequestCredentials;
  body?: unknown;
  schema?: ZodType<T>;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export function createHttpClient({ baseUrl, getToken, onUnauthorized, credentials }: HttpClientOptions) {
  const root = baseUrl.replace(/\/$/, "");

  async function request<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
    const { method = "GET", body, schema, signal, query, credentials: requestCredentials } = options;

    const url = new URL(root + path);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null && value !== "") {
          url.searchParams.set(key, String(value));
        }
      }
    }

    const headers: Record<string, string> = { accept: "application/json" };
    if (body !== undefined) headers["content-type"] = "application/json";
    const token = getToken?.();
    if (token) headers.authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        credentials: requestCredentials ?? credentials,
        signal,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError(0, "network", "Could not reach the server. Check your connection.");
    }

    if (response.status === 401) onUnauthorized?.();

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload = text ? safeJson(text) : null;

    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(payload);
      if (parsed.success) {
        throw new ApiError(
          response.status,
          parsed.data.code,
          parsed.data.message,
          parsed.data.fields,
          parsed.data.requestId,
        );
      }
      throw new ApiError(response.status, "unexpected", "Something went wrong. Please try again.");
    }

    if (!schema) return payload as T;

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      // A shape mismatch is a contract bug, not a user error. Surfacing it loudly
      // in development is the point -- silently rendering undefined is worse.
      throw new ApiError(response.status, "contract_mismatch",
        "The server returned data this app does not understand.");
    }
    return parsed.data;
  }

  return { request };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export type HttpClient = ReturnType<typeof createHttpClient>;
